"use client";

import React, { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { ArrowLeft, Loader2, MessageSquare, ChevronDown, ChevronRight, Save, CheckCircle } from "lucide-react";
import { MultiSelectDropdown } from "../../ui/multi-select-dropdown";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface InvCostAllocationProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface LedgerOption {
  ledger_name: string;
  latest_date?: string;
  amount?: number;
}

interface NoteLineGroup {
  noteLine: string;
  ledgers: string[];
}

const TARGET_NOTE_LINES = [
  "Salaries and Wages",
  "Contribution to Provident & Other Funds",
  "Staff Welfare Expenses",
  "Gratuity",
  "Employee Stock Option Plans (ESOP)",
  "Depreciation on Tangible Assets",
  "Depreciation on Right-of-Use Assets",
  "Depreciation on Intangible Assets",
  "Interest expense on borrowings",
  "Interest expense on finance lease",
  "Other interest expense",
  "Consumption of stores & spares",
  "Power & Fuel",
  "Repairs and Maintenance - Building",
  "Repairs and Maintenance - Machinery",
  "Repairs and Maintenance - Others",
  "Rental Expense",
  "Insurance Expense",
  "Rates & Taxes",
  "Miscellaneous Expenses",
  "Legal & Professional Expense",
  "Payment to Auditors",
  "Forex Fluctuation Loss/Gain",
  "Other Expenses"
];

// Inventory Cost Allocation: AS → 20028,20031,20032,20033,20034; Ind AS → 20030,20033,20034,20035,20036 (respectively)
const INV_COST_AS = {
  costOfPurchaseFsSubLineId: 20028,
  excludedFsSubLineIds: new Set([20028, 20031, 20032, 20033, 20034]),
};
const INV_COST_IND_AS = {
  costOfPurchaseFsSubLineId: 20030,
  excludedFsSubLineIds: new Set([20030, 20033, 20034, 20035, 20036]),
};

function getInvCostAllocationIds(frf: string | null) {
  return frf === "Indian Accounting Standards (Ind AS)" ? INV_COST_IND_AS : INV_COST_AS;
}

export default function InvCostAllocation({ onBack, setActiveSection }: InvCostAllocationProps) {
  const [ledgerOptions, setLedgerOptions] = useState<string[]>([]);
  const [selectedLedgers, setSelectedLedgers] = useState<string[]>([]);
  const [isLoadingLedgers, setIsLoadingLedgers] = useState(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  
  // Cost of Conversion state
  const [noteLineGroups, setNoteLineGroups] = useState<NoteLineGroup[]>([]);
  const [isLoadingNoteLines, setIsLoadingNoteLines] = useState(false);
  const [expandedNoteLines, setExpandedNoteLines] = useState<Set<string>>(new Set());
  const [selectedNoteLines, setSelectedNoteLines] = useState<Set<string>>(new Set());
  const [selectedIndividualLedgers, setSelectedIndividualLedgers] = useState<Set<string>>(new Set());
  
  // Other Costs state
  const [otherCostsLedgerOptions, setOtherCostsLedgerOptions] = useState<string[]>([]);
  const [selectedOtherCostsLedgers, setSelectedOtherCostsLedgers] = useState<string[]>([]);
  const [isLoadingOtherCostsLedgers, setIsLoadingOtherCostsLedgers] = useState(false);
  
  // Save state
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `INV_COST_ALLOCATION_${normalizedSection}`;
  };

  // Load ledgers for Cost of Purchase (fs_sub_line_id: AS 20028, Ind AS 20030)
  useEffect(() => {
    loadLedgers();
  }, []);

  // Load note line groups for Cost of Conversion
  useEffect(() => {
    loadNoteLineGroups();
  }, []);

  // Load ledgers for Other Costs
  useEffect(() => {
    loadOtherCostsLedgers();
  }, []);

  // Load saved data on mount
  useEffect(() => {
    loadSavedData();
  }, []);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_INV_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_INV_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "COST-ALLOCATION-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  const loadLedgers = async () => {
    setIsLoadingLedgers(true);
    try {
      if (window.sharePointAPI?.loadLedgerData) {
        let costOfPurchaseId = INV_COST_AS.costOfPurchaseFsSubLineId;
        try {
          const frfResult = await (window as any).sharePointAPI?.getFrf?.();
          const frf = frfResult?.success ? frfResult.frf : null;
          costOfPurchaseId = getInvCostAllocationIds(frf).costOfPurchaseFsSubLineId;
        } catch {
          // keep AS default
        }
        const result = await window.sharePointAPI.loadLedgerData({
          fs_sub_line_id: costOfPurchaseId
        });
        
        if (result?.success && result.data && Array.isArray(result.data)) {
          // Extract ledger names from the results
          // loadLedgerData already cleans the ledger names, so we can use them directly
          const ledgers = result.data
            .map((item: LedgerOption) => item.ledger_name)
            .filter((name: string | undefined): name is string => !!name && name.trim().length > 0);
          
          // Remove duplicates and sort
          const uniqueLedgers = Array.from(new Set(ledgers)).sort();
          setLedgerOptions(uniqueLedgers);
        } else {
          console.error("Failed to load ledgers:", result?.error || "Unknown error");
        }
      } else {
        console.error("SharePoint API not available");
      }
    } catch (error) {
      console.error("Error loading ledgers:", error);
    } finally {
      setIsLoadingLedgers(false);
    }
  };

  const loadNoteLineGroups = async () => {
    setIsLoadingNoteLines(true);
    try {
      if (window.sharePointAPI?.downloadTrialBalanceMapping) {
        const mappingResult = await window.sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
        
        if (mappingResult?.success && mappingResult?.data) {
          const mappingData = mappingResult.data;
          const noteLineMap = new Map<string, Set<string>>();

          // Process all sections in the mapping data
          for (const [sectionName, ledgers] of Object.entries(mappingData)) {
            if (Array.isArray(ledgers)) {
              ledgers.forEach((item: any) => {
                const noteLine = item.note_line || item.noteLine;
                
                if (noteLine && TARGET_NOTE_LINES.includes(noteLine)) {
                  let ledgerName = (item.ledger_name || item.ledgerName || '').toString();
                  ledgerName = ledgerName
                    .replace(/_x000D/g, '')
                    .replace(/\n/g, '')
                    .replace(/_+$/, '')
                    .trim();
                  
                  if (ledgerName) {
                    if (!noteLineMap.has(noteLine)) {
                      noteLineMap.set(noteLine, new Set());
                    }
                    noteLineMap.get(noteLine)!.add(ledgerName);
                  }
                }
              });
            }
          }

          // Convert to array format and sort
          const groups: NoteLineGroup[] = TARGET_NOTE_LINES
            .filter(noteLine => noteLineMap.has(noteLine))
            .map(noteLine => ({
              noteLine,
              ledgers: Array.from(noteLineMap.get(noteLine)!).sort()
            }));

          setNoteLineGroups(groups);
        } else {
          console.error("Failed to load note line groups:", mappingResult?.error || "Unknown error");
        }
      } else {
        console.error("SharePoint API not available");
      }
    } catch (error) {
      console.error("Error loading note line groups:", error);
    } finally {
      setIsLoadingNoteLines(false);
    }
  };

  const toggleNoteLineExpansion = (noteLine: string) => {
    const newExpanded = new Set(expandedNoteLines);
    if (newExpanded.has(noteLine)) {
      newExpanded.delete(noteLine);
    } else {
      newExpanded.add(noteLine);
    }
    setExpandedNoteLines(newExpanded);
  };

  const handleNoteLineToggle = (noteLine: string, ledgers: string[]) => {
    const newSelected = new Set(selectedNoteLines);
    const newIndividualSelected = new Set(selectedIndividualLedgers);
    
    if (newSelected.has(noteLine)) {
      // Deselect note line and all its ledgers
      newSelected.delete(noteLine);
      ledgers.forEach(ledger => newIndividualSelected.delete(ledger));
    } else {
      // Select note line and all its ledgers
      newSelected.add(noteLine);
      ledgers.forEach(ledger => newIndividualSelected.add(ledger));
    }
    
    setSelectedNoteLines(newSelected);
    setSelectedIndividualLedgers(newIndividualSelected);
  };

  const handleIndividualLedgerToggle = (ledger: string, noteLine: string, allLedgers: string[]) => {
    const newIndividualSelected = new Set(selectedIndividualLedgers);
    
    if (newIndividualSelected.has(ledger)) {
      newIndividualSelected.delete(ledger);
    } else {
      newIndividualSelected.add(ledger);
    }
    
    setSelectedIndividualLedgers(newIndividualSelected);
    
    // Update note line selection based on whether all ledgers are selected
    const newSelected = new Set(selectedNoteLines);
    const allSelected = allLedgers.every(l => newIndividualSelected.has(l));
    
    if (allSelected && !newSelected.has(noteLine)) {
      newSelected.add(noteLine);
    } else if (!allSelected && newSelected.has(noteLine)) {
      newSelected.delete(noteLine);
    }
    
    setSelectedNoteLines(newSelected);
  };

  const loadOtherCostsLedgers = async () => {
    setIsLoadingOtherCostsLedgers(true);
    try {
      if (window.sharePointAPI?.downloadTrialBalanceMapping) {
        let excludedIds = INV_COST_AS.excludedFsSubLineIds;
        try {
          const frfResult = await (window as any).sharePointAPI?.getFrf?.();
          const frf = frfResult?.success ? frfResult.frf : null;
          excludedIds = getInvCostAllocationIds(frf).excludedFsSubLineIds;
        } catch {
          // keep AS default
        }
        const mappingResult = await window.sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
        
        if (mappingResult?.success && mappingResult?.data) {
          const mappingData = mappingResult.data;
          const ledgerSet = new Set<string>();

          // Process all sections in the mapping data
          for (const [sectionName, ledgers] of Object.entries(mappingData)) {
            if (Array.isArray(ledgers)) {
              ledgers.forEach((item: any) => {
                // Include only Expense type
                const itemType = item.type || item.itemType;
                if (itemType !== "Expense") {
                  return;
                }

                // Exclude fs_sub_line_id (AS: 20028,20031,20032,20033,20034; Ind AS: 20030,20033,20034,20035,20036)
                const fsSubLineId = item.fs_sub_line_id || item.fsSubLineId;
                if (fsSubLineId && excludedIds.has(fsSubLineId)) {
                  return;
                }

                // Extract and clean ledger name
                let ledgerName = (item.ledger_name || item.ledgerName || '').toString();
                ledgerName = ledgerName
                  .replace(/_x000D/g, '')
                  .replace(/\n/g, '')
                  .replace(/_+$/, '')
                  .trim();
                
                if (ledgerName) {
                  ledgerSet.add(ledgerName);
                }
              });
            }
          }

          // Convert to sorted array
          const uniqueLedgers = Array.from(ledgerSet).sort();
          setOtherCostsLedgerOptions(uniqueLedgers);
        } else {
          console.error("Failed to load other costs ledgers:", mappingResult?.error || "Unknown error");
        }
      } else {
        console.error("SharePoint API not available");
      }
    } catch (error) {
      console.error("Error loading other costs ledgers:", error);
    } finally {
      setIsLoadingOtherCostsLedgers(false);
    }
  };

  const loadSavedData = async () => {
    try {
      if (window.sharePointAPI?.downloadClientJson) {
        const result = await window.sharePointAPI.downloadClientJson("Execution_INV_CstAloc.json");
        
        if (result?.success && result.data) {
          const data = result.data;
          
          // Load Cost of Purchase
          if (data.costOfPurchase && Array.isArray(data.costOfPurchase)) {
            setSelectedLedgers(data.costOfPurchase);
          }
          
          // Load Cost of Conversion
          if (data.costOfConversion) {
            if (data.costOfConversion.selectedNoteLines && Array.isArray(data.costOfConversion.selectedNoteLines)) {
              setSelectedNoteLines(new Set(data.costOfConversion.selectedNoteLines));
            }
            if (data.costOfConversion.selectedIndividualLedgers && Array.isArray(data.costOfConversion.selectedIndividualLedgers)) {
              setSelectedIndividualLedgers(new Set(data.costOfConversion.selectedIndividualLedgers));
            }
          }
          
          // Load Other Costs
          if (data.otherCosts && Array.isArray(data.otherCosts)) {
            setSelectedOtherCostsLedgers(data.otherCosts);
          }
        }
      }
    } catch (error) {
      console.error("Error loading saved data:", error);
      // Silently fail - it's okay if file doesn't exist yet
    }
  };

  const handleSave = async () => {
    setSaveStatus("saving");
    setSaveError(null);
    
    try {
      const fileName = "Execution_INV_CstAloc.json";
      
      // Prepare data to save
      const dataToSave = {
        costOfPurchase: selectedLedgers,
        costOfConversion: {
          selectedNoteLines: Array.from(selectedNoteLines),
          selectedIndividualLedgers: Array.from(selectedIndividualLedgers)
        },
        otherCosts: selectedOtherCostsLedgers,
        lastUpdated: new Date().toISOString()
      };
      
      // Convert to JSON and encode as base64
      const jsonString = JSON.stringify(dataToSave, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      
      // Get FY year
      const fyYear = getFY_YEAR();
      
      // Upload to SharePoint
      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: "Inventory Cost Allocation",
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: ["Execution_INV_CstAloc"],
      });
      
      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save cost allocation data");
      }
      
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to save cost allocation:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 5000);
    }
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "INV";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "COST-ALLOCATION-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Cost Allocation - Inventory</h2>
          <p className="text-gray-400">Manage cost allocation procedures for inventory</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Cost of Purchase</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Cost of Purchase"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Select Ledgers</Label>
            <MultiSelectDropdown
              options={ledgerOptions}
              value={selectedLedgers}
              onChange={setSelectedLedgers}
              placeholder="Select ledgers..."
              loading={isLoadingLedgers}
              showSelectAll={true}
              disabled={isLoadingLedgers || ledgerOptions.length === 0}
            />
            {isLoadingLedgers && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading ledgers...
              </div>
            )}
            {selectedLedgers.length > 0 && (
              <p className="text-sm text-green-400">
                {selectedLedgers.length} ledger{selectedLedgers.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Cost of Conversion</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Cost of Conversion"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingNoteLines ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-white/60 mr-2" />
              <span className="text-white/60">Loading note lines...</span>
            </div>
          ) : noteLineGroups.length === 0 ? (
            <p className="text-sm text-white/60 text-center py-4">No note lines found.</p>
          ) : (
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 bg-white/5">
                    <TableHead className="w-12 text-white/80"></TableHead>
                    <TableHead className="w-12 text-white/80"></TableHead>
                    <TableHead className="text-white/80">Note Line</TableHead>
                    <TableHead className="text-white/80 text-right">Ledger Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {noteLineGroups.map((group) => {
                    const isExpanded = expandedNoteLines.has(group.noteLine);
                    const isNoteLineSelected = selectedNoteLines.has(group.noteLine);
                    const allLedgersSelected = group.ledgers.every(ledger => 
                      selectedIndividualLedgers.has(ledger)
                    );
                    
                    return (
                      <React.Fragment key={group.noteLine}>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          <TableCell className="w-12">
                            <button
                              onClick={() => toggleNoteLineExpansion(group.noteLine)}
                              className="p-1 hover:bg-white/10 rounded transition-colors"
                              title={isExpanded ? "Collapse" : "Expand"}
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-white/60" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-white/60" />
                              )}
                            </button>
                          </TableCell>
                          <TableCell className="w-12">
                            <input
                              type="checkbox"
                              checked={isNoteLineSelected}
                              onChange={() => handleNoteLineToggle(group.noteLine, group.ledgers)}
                              className="h-4 w-4 rounded border-white/10 bg-white/5 text-[#4da3ff] focus:ring-[#4da3ff] cursor-pointer"
                            />
                          </TableCell>
                          <TableCell className="font-medium text-white">
                            {group.noteLine}
                          </TableCell>
                          <TableCell className="text-right text-white/60">
                            {group.ledgers.length} ledger{group.ledgers.length !== 1 ? 's' : ''}
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow className="border-white/10 bg-white/5">
                            <TableCell colSpan={4} className="p-0">
                              <div className="px-4 py-3 space-y-2">
                                <div className="text-xs font-semibold text-white/60 mb-2">
                                  Ledger Accounts:
                                </div>
                                <div className="space-y-1 max-h-60 overflow-y-auto">
                                  {group.ledgers.map((ledger) => {
                                    const isLedgerSelected = selectedIndividualLedgers.has(ledger);
                                    return (
                                      <div
                                        key={ledger}
                                        className="flex items-center gap-3 py-1.5 px-2 hover:bg-white/5 rounded transition-colors"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isLedgerSelected}
                                          onChange={() => handleIndividualLedgerToggle(ledger, group.noteLine, group.ledgers)}
                                          className="h-3.5 w-3.5 rounded border-white/10 bg-white/5 text-[#4da3ff] focus:ring-[#4da3ff] cursor-pointer"
                                        />
                                        <span className="text-sm text-white/90 flex-1">{ledger}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {selectedNoteLines.size > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-white/60">
                {selectedNoteLines.size} note line{selectedNoteLines.size !== 1 ? 's' : ''} selected
              </span>
              {selectedIndividualLedgers.size > 0 && (
                <span className="text-white/60">
                  • {selectedIndividualLedgers.size} ledger{selectedIndividualLedgers.size !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Other Costs</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Other Costs"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Select Ledgers</Label>
            <MultiSelectDropdown
              options={otherCostsLedgerOptions}
              value={selectedOtherCostsLedgers}
              onChange={setSelectedOtherCostsLedgers}
              placeholder="Select ledgers..."
              loading={isLoadingOtherCostsLedgers}
              showSelectAll={true}
              disabled={isLoadingOtherCostsLedgers || otherCostsLedgerOptions.length === 0}
            />
            {isLoadingOtherCostsLedgers && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading ledgers...
              </div>
            )}
            {selectedOtherCostsLedgers.length > 0 && (
              <p className="text-sm text-green-400">
                {selectedOtherCostsLedgers.length} ledger{selectedOtherCostsLedgers.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex items-center justify-end gap-3">
        {saveStatus === "saved" && (
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm">Saved successfully!</span>
          </div>
        )}
        {saveStatus === "error" && saveError && (
          <div className="text-red-400 text-sm">
            Error: {saveError}
          </div>
        )}
        <Button
          onClick={handleSave}
          disabled={saveStatus === "saving"}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {saveStatus === "saving" ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Cost Allocation
            </>
          )}
        </Button>
      </div>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="inventory"
            itemId="COST-ALLOCATION-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Cost of Purchase")]: "Cost of Purchase",
              [getSectionQuestionId("Cost of Conversion")]: "Cost of Conversion",
              [getSectionQuestionId("Other Costs")]: "Other Costs",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}

